<script setup lang="ts">
import { countries } from "~/utils/countries";

const props = defineProps<{
  modelValue: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const isDropdownOpen = ref(false);
const triggerRef = ref<HTMLButtonElement | null>(null);
const dropdownRef = ref<HTMLUListElement | null>(null);
const dropdownPos = reactive({ top: 0, left: 0, width: 0 });

const selectedCountry = computed(
  () => countries.find((c) => c.iso === props.modelValue) ?? countries[0]
);

function toggleDropdown() {
  if (props.disabled) return;
  if (!isDropdownOpen.value && triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect();
    dropdownPos.top = rect.bottom + 4;
    dropdownPos.left = rect.left;
    dropdownPos.width = rect.width;
  }
  isDropdownOpen.value = !isDropdownOpen.value;
}

function selectCountry(iso: string) {
  emit("update:modelValue", iso);
  isDropdownOpen.value = false;
}

function closeDropdown() {
  isDropdownOpen.value = false;
}

// Scroll events don't bubble, so a scrolling modal/panel behind the list is only seen in the
// capture phase. The list is position: fixed, so it would otherwise stay put while its field moves.
// Scrolling the list itself must not close it.
function onAnyScroll(e: Event) {
  if (!isDropdownOpen.value) return;
  if (dropdownRef.value && e.target instanceof Node && dropdownRef.value.contains(e.target)) return;
  closeDropdown();
}

// Close dropdown when clicking outside the trigger or list
function onDocumentClick(e: MouseEvent) {
  if (!isDropdownOpen.value) return;
  const target = e.target as Node;
  if (triggerRef.value?.contains(target) || dropdownRef.value?.contains(target)) return;
  closeDropdown();
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
  window.addEventListener("resize", closeDropdown);
  window.addEventListener("scroll", onAnyScroll, { passive: true, capture: true });
});

onUnmounted(() => {
  document.removeEventListener("click", onDocumentClick);
  window.removeEventListener("resize", closeDropdown);
  window.removeEventListener("scroll", onAnyScroll, { capture: true });
});
</script>

<template>
  <div class="shrink-0 relative">
    <!-- Trigger button: shows flag + dial code only -->
    <button
      ref="triggerRef"
      type="button"
      :disabled="disabled"
      @click="toggleDropdown"
      class="flex items-center gap-1.5 h-full w-full rounded-[6px] border-0 px-3 py-3 text-sm font-medium text-on-primary-heading ring ring-inset ring-on-primary-heading transition-colors outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-75 bg-white dark:bg-black whitespace-nowrap"
    >
      <span>{{ selectedCountry.flag }} {{ selectedCountry.dialCode }}</span>
      <svg class="w-3.5 h-3.5 ml-1 shrink-0 text-on-primary-heading/60 transition-transform" :class="{ 'rotate-180': isDropdownOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    <!-- Dropdown teleported to body so it renders outside any parent modal/overflow clipping -->
    <Teleport to="body">
      <ul
        ref="dropdownRef"
        v-if="isDropdownOpen"
        :style="{
          position: 'fixed',
          top: dropdownPos.top + 'px',
          left: dropdownPos.left + 'px',
          minWidth: dropdownPos.width + 'px',
          maxHeight: '260px',
          overflowY: 'auto',
          zIndex: 99999,
        }"
        class="bg-white dark:bg-black border border-on-primary-heading/20 rounded-[6px] shadow-xl w-max pointer-events-auto"
        @pointerdown.stop
      >
        <li
          v-for="c in countries"
          :key="c.iso"
          @click="selectCountry(c.iso)"
          class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          :class="{ 'bg-on-primary-heading text-white hover:bg-on-primary-heading': c.iso === modelValue }"
        >
          <span>{{ c.flag }}</span>
          <span class="font-medium">{{ c.name }}</span>
          <span class="ml-auto pl-3">{{ c.dialCode }}</span>
        </li>
      </ul>
    </Teleport>
  </div>
</template>
